import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Placeholder,
} from "react-bootstrap";
import { rsvpSchema } from "../validators/validatorSchema";
import { clearError, validateData } from "../validators/YupValidations";
import SelectField from "../components/common/SelectField";
import { useDispatch, useSelector } from "react-redux";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { useNavigate } from "react-router-dom";
// import { formatToUSDateTime } from "../utils/dateFormat";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";
import { formatEventDate } from "../utils/formatEventDate";
import { SET_LOADING } from "../redux/user";
import EmailVerificationModal from "../components/auth/EmailVerificationModal";
import { useLocation } from "react-router-dom";
import PaymentStatusModal from "../components/common/PaymentStatusModal";
import SecurePaymentLock from "../components/common/SecurePaymentLock";
import ChooseTicketOption from "../components/events/ChooseTicketOption";
import clockImg from "../assets/images/event/clockImg.svg";
import locationImg from "../assets/images/event/locationImg.svg";

const Rsvp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isGuestFlow = queryParams.get("guest") === "true";

  const { user } = useSelector((state) => state.USER_DATA);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', or 'cancelled'

  const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";

  const [formData, setFormData] = useState({
    event: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    countryCode: "+1",
    phone: "",
  });
  const [guestList, setGuestList] = useState([]);
  const [guestErrors, setGuestErrors] = useState([]);
  const [showGuestSection, setShowGuestSection] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [userEmailVerified, setUserEmailVerified] = useState("");
  const [sessionVerifiedEmail, setSessionVerifiedEmail] = useState("");
  const isEmailVerified =
    !!formData.email &&
    (formData.email === userEmailVerified ||
      formData.email === sessionVerifiedEmail);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);

  // Ticket packages for the selected event (guest flow only).
  const [packages, setPackages] = useState([]);
  const [selectedOption, setSelectedOption] = useState("single"); // 'single' | package._id
  const selectedPackage = packages.find((p) => p._id === selectedOption) || null;

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const eventOptions = events.map((item) => ({
    value: item._id,
    label: item.title,
  }));

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        first_name: user?.first_name || "",
        middle_name: user?.middle_name || "",
        last_name: user?.last_name || "",
        phone:
          user.country_code === "+1"
            ? formatUSPhone(user.phone || "")
            : user.phone || "",
        countryCode: user.country_code,
      }));

      if (user.is_verify_email) {
        setUserEmailVerified(user.email || "");
      }
    }
  }, [user]);

  /* ---------------- GUEST LOGIC ---------------- */

  const openGuestSection = () => {
    if (!showGuestSection) {
      setShowGuestSection(true);
      setGuestList([""]);
    }
  };

  const addGuest = () => {
    // Prevent adding a new guest if the last one is empty
    if (guestList.length > 0 && guestList[guestList.length - 1].trim() === "") {
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[guestList.length - 1] = "Full name is required";
        return updated;
      });
      return;
    }
    setShowGuestSection(true);
    // add new guest input and focus it after render
    setGuestList((prev) => {
      const next = [...prev, ""];
      // focus the newly added input on next tick
      setTimeout(() => {
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs.length) {
          inputs[inputs.length - 1].focus();
        }
      }, 0);
      return next;
    });
    setGuestErrors((prev) => [...prev, ""]);
  };

  const removeGuest = (index) => {
    setGuestList((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setShowGuestSection(false);
      return updated;
    });
    setGuestErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuestChange = (value, index) => {
    setGuestList((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    // Clear error when user starts typing
    if (guestErrors[index]) {
      setGuestErrors((prev) => {
        const updated = [...prev];
        updated[index] = "";
        return updated;
      });
    }
  };

  const handleGuestKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If this is the last input, add a new guest (same behavior as + Add Guest)
      if (index === guestList.length - 1) {
        addGuest();
      } else {
        // move focus to next guest input if present
        const inputs = document.querySelectorAll(".guest-row input");
        if (inputs && inputs[index + 1]) inputs[index + 1].focus();
      }
    }
  };

  const formForValidation = {
    ...formData,
    phone:
      formData.countryCode === "+1"
        ? normalizePhoneForSave(formData.phone)
        : formData.phone.replace(/\D/g, ""),
  };

  /* ---------------- CALCULATIONS ---------------- */

  const validGuests = guestList.filter((g) => g.trim() !== "");
  // Primary is always included in this form flow
  const peopleCount = 1 + validGuests.length;
  // Package → one ticket per included event per person, priced at the non-member offer.
  const displayTickets = selectedPackage
    ? peopleCount * (selectedPackage.event_count || 1)
    : peopleCount;
  const perPerson = selectedPackage
    ? Number(selectedPackage.offer_price_nonmember || 0)
    : Number(eventDetails?.price) || 0;
  const totalAmount = (peopleCount * perPerson).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // IF LOGGED IN -> Redirect to Event Details to Reserve
    // Skip this if isGuestFlow is true
    if (user && !isGuestFlow) {
      navigate(`/events/${formData.event}`);
      return;
    }

    const isValid = await validateData(
      rsvpSchema,
      formForValidation,
      setErrors
    );
    if (!isValid) return;

    if (!isEmailVerified) {
      toastErrorMessage("Please verify your email before proceeding.");
      return;
    }

    // IF NOT LOGGED IN -> PROCEED TO PAYMENT
    try {
      dispatch(SET_LOADING(true));
      const payload = {
        amount: Number(totalAmount), // backend re-verifies
        event_id: formData.event,
        ...(selectedPackage ? { package_id: selectedPackage._id } : {}),
        primary_included: true,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formForValidation.phone,
        country_code: formData.countryCode,
        guests: guestList
          .filter((g) => g.trim() !== "")
          .map((g) => ({ name: g })),
      };

      const response = await API.post(
        `${api_paths.member.payment.EVENT_CHECKOUT}?type=event&screen=rsvp`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data.payload.token;
        // Submit to Converge
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else if (response.data?.payload?.result?.success) {
        toastSuccessMessage("Ticket booked successfully!");
        dispatch(SET_LOADING(false));
        navigate("/");
      }
    } catch (error) {
      toastErrorMessage(
        error?.response?.data?.message ||
        "Failed to submit RSVP. Please try again."
      );
      dispatch(SET_LOADING(false));
      // console.error("Error submitting RSVP:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/rsvp`, { replace: true });
  };

  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate("/rsvp", { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  useEffect(() => {
    const fetchAvailableEvents = async () => {
      try {
        const response = await API.get(api_paths.member.EVENTS.GET_LIST);

        const eventList = response.data?.payload?.result || [];
        setEvents(eventList);

        if (eventList.length > 0) {
          setFormData((prev) => ({
            ...prev,
            event: eventList[0]._id,
          }));
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchAvailableEvents();
  }, []);

  useEffect(() => {
    if (!formData.event) return;

    const fetchEvent = async (eventId) => {
      try {
        setEventDetailsLoading(true);
        const response = await API.get(
          `${api_paths.member.EVENTS.EVENT}/${eventId}`
        );

        const eventData = response.data?.payload?.result || null;
        setEventDetails(eventData);

        setEventDetailsLoading(false);
      } catch (error) {
        setEventDetailsLoading(false);
      }
    };

    fetchEvent(formData.event);
  }, [formData.event]);

  // Load packages for the selected event; reset the choice so a stale package can't carry over.
  useEffect(() => {
    setSelectedOption("single");
    if (!formData.event) {
      setPackages([]);
      return;
    }
    API.get(`${api_paths.member.EVENTS.PACKAGES_FOR_EVENT}/${formData.event}`)
      .then((res) => setPackages(res?.data?.payload?.result || []))
      .catch(() => setPackages([]));
  }, [formData.event]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setFormData({
        event: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        countryCode: "+1",
        phone: "",
      });

      setErrors({});
    };
  }, []);

  return (
    <div>
      <Container className="py-5">
        <Row className="mb-4">
          <Col lg={12}>
            <div className="rsvp-header d-flex flex-column justify-content-center align-items-center text-center">
              <h1 className="inter-bold h1">Event RSVP</h1>
              <p>
                {user && !isGuestFlow ? "Register " : "Guest can register "}
                for upcoming community events and celebrations
              </p>
            </div>
          </Col>
        </Row>

        {/* Two-column section */}
        <Row className="gy-4 flex-column-reverse flex-lg-row">
          {/* LEFT: Form */}
          <Col lg={6}>
            <Card className="rsvp-form p-md-3 p-1">
              <Card.Body>
                <Card.Title className="fw-bold">
                  Register Your Attendance
                </Card.Title>
                <Card.Text>
                  Fill out the form below to confirm your attendance at an
                  upcoming event
                </Card.Text>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="formEvent">
                    <SelectField
                      label="Select Event"
                      required
                      value={
                        eventOptions.find(
                          (opt) => opt.value === formData.event
                        ) || null
                      }
                      options={eventOptions}
                      onChange={(opt) => {
                        setFormData({
                          ...formData,
                          event: opt ? opt.value : "",
                        });
                        errors.event && clearError(setErrors, "event");
                      }}
                      error={errors.event?.message}
                    />
                  </Form.Group>

                  {(!user || isGuestFlow) && (
                    <ChooseTicketOption
                      event={eventDetails}
                      packages={packages}
                      isMember={false}
                      selectedKey={selectedOption}
                      onSelect={setSelectedOption}
                      wrapperClassName="mb-3"
                    />
                  )}

                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formFirstName">
                        <Form.Label>
                          First Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="First Name"
                          value={formData.first_name}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              first_name: e.target.value,
                            });
                            errors.first_name &&
                              clearError(setErrors, "first_name");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                        {errors.first_name && (
                          <Form.Text className="text-danger">
                            {errors.first_name.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formMiddleName">
                        <Form.Label>Middle Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Middle Name"
                          value={formData.middle_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              middle_name: e.target.value,
                            })
                          }
                          disabled={!!user && !isGuestFlow}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="formLastName">
                        <Form.Label>
                          Last Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Last Name"
                          value={formData.last_name}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              last_name: e.target.value,
                            });
                            errors.last_name &&
                              clearError(setErrors, "last_name");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                        {errors.last_name && (
                          <Form.Text className="text-danger">
                            {errors.last_name.message}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          errors.email && clearError(setErrors, "email");
                        }}
                        disabled={!!user && !isGuestFlow}
                      />
                      {(!user || isGuestFlow) && (
                        <Button
                          // variant={isEmailVerified ? "success" : "secondary"}
                          className={`btn ${isEmailVerified ? "btn-black" : "btn-orange"
                            }`}
                          disabled={isEmailVerified}
                          onClick={async () => {
                            try {
                              // Create a temporary schema just for email or use Yup.reach
                              const emailSchema = Yup.string()
                                .email("Enter valid email")
                                .required("Email is required");

                              await emailSchema.validate(formData.email);
                              errors.email && clearError(setErrors, "email");
                              setShowVerifyModal(true);
                            } catch (err) {
                              setErrors((prev) => ({
                                ...prev,
                                email: { message: err.message },
                              }));
                            }
                          }}
                        >
                          {isEmailVerified ? "Verified" : "Verify"}
                        </Button>
                      )}
                    </div>
                    {errors.email && (
                      <Form.Text className="text-danger">
                        {errors.email.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formPhone">
                    <Form.Label>
                      Phone Number <span className="text-danger">*</span>
                    </Form.Label>

                    <Row className="g-2">
                      <Col xs={4} md={3}>
                        <SelectField
                          value={countryCodeOptions.find(
                            (opt) => opt.value === formData.countryCode
                          )}
                          options={countryCodeOptions}
                          onChange={(opt) => {
                            const newCode = opt.value;

                            setFormData((prev) => ({
                              ...prev,
                              countryCode: newCode,
                              phone:
                                newCode === "+1"
                                  ? formatUSPhone(
                                    normalizePhoneForSave(prev.phone)
                                  )
                                  : prev.phone.replace(/\D/g, ""),
                            }));

                            errors.phone && clearError(setErrors, "phone");
                          }}
                          disabled={!!user && !isGuestFlow}
                        />
                      </Col>

                      <Col xs={8} md={9}>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => {
                            let value = e.target.value;

                            if (formData.countryCode === "+1") {
                              value = formatUSPhone(value).slice(0, 15);
                            } else {
                              value = value.replace(/\D/g, "").slice(0, 10);
                            }
                            setFormData({ ...formData, phone: value });
                            errors.phone && clearError(setErrors, "phone");
                          }}
                          maxLength={formData.countryCode === "+1" ? 15 : 10}
                          disabled={!!user && !isGuestFlow}
                        />
                      </Col>
                    </Row>

                    {errors.phone && (
                      <Form.Text className="text-danger">
                        {errors.phone.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  {(!user || isGuestFlow) && (
                    <>
                      <div className="mb-2">
                        <label
                          className="btn btn-orange d-flex flex-row mb-3"
                          onClick={openGuestSection}
                          style={{ width: "fit-content" }}
                        >
                          {/* <div className="icon-plus me-2">+</div> */}
                          <div> Add Guests</div>
                        </label>
                        <div>
                          <p className="mb-4 f-16 fw-semibold orange">
                            Note: Children under 5 years enter free.
                          </p>
                        </div>
                        {showGuestSection && (
                          <>
                            {guestList.map((g, i) => (
                              <div className="guest-row" key={i}>
                                <div className="guest-input-wrapper w-100 mb-2">
                                  <input
                                    className="form-control"
                                    placeholder="Full Name"
                                    value={g}
                                    onChange={(e) =>
                                      handleGuestChange(e.target.value, i)
                                    }
                                    onKeyDown={(e) => handleGuestKeyDown(e, i)}
                                    style={{
                                      border: guestErrors[i]
                                        ? "1px solid #dc3545"
                                        : "1px solid var(--border-color)",
                                    }}
                                  />

                                  <button
                                    type="button"
                                    className="guest-remove-btn"
                                    onClick={() => removeGuest(i)}
                                    aria-label="remove guest"
                                  >
                                    ✕
                                  </button>

                                  {guestErrors[i] && (
                                    <small className="text-danger d-block mt-1">
                                      {guestErrors[i]}
                                    </small>
                                  )}
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={addGuest}
                              style={{
                                width: "100%",
                                background: "var(--border-color)",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "none",
                                fontWeight: 500,
                                marginBottom: "15px",
                                cursor: "pointer",
                                color: "var(--text-main)",
                                fontSize: "14px",
                              }}
                            >
                              + Add Guest
                            </button>
                          </>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Total Tickets</span>
                        <strong>{displayTickets}</strong>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <span>Total Amount</span>
                        <strong>${totalAmount}</strong>
                      </div>
                    </>
                  )}

                  {(!user || isGuestFlow) && (
                    <div className="mt-2">
                      <SecurePaymentLock />
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="btn  btn-orange-md px-4 border-0"
                    disabled={(!user || isGuestFlow) && !isEmailVerified}
                  >
                    RSVP & Purchase Ticket
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT: Details card */}
          <Col lg={6}>
            {eventDetailsLoading ? (
              <Card className="shadow-sm h-100">
                {/* Image skeleton */}
                <div
                  className="w-100 bg-light placeholder-glow"
                  style={{ height: "200px" }}
                >
                  <span className="placeholder w-100 h-100"></span>
                </div>

                <Card.Body>
                  {/* Title */}
                  <Placeholder
                    as={Card.Title}
                    animation="wave"
                    className="w-75"
                  >
                    <Placeholder xs={9} />
                  </Placeholder>

                  {/* Price */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={6} />
                  </Placeholder>

                  {/* Description */}
                  <Placeholder as={Card.Text} animation="wave">
                    <Placeholder xs={12} />
                    <Placeholder xs={10} />
                    <Placeholder xs={8} />
                  </Placeholder>

                  {/* Time */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={7} />
                  </Placeholder>

                  {/* Location */}
                  <Placeholder as={Card.Text} animation="wave" className="w-75">
                    <Placeholder xs={9} />
                  </Placeholder>
                </Card.Body>
              </Card>
            ) : eventDetails ? (
              <Card className="rsvp-card h-100">
                {eventDetails?.image_path && (
                  <Card.Img
                    variant="top"
                    className="w-100 rsvp-card-img"
                    src={`${IMG_PATH}${eventDetails?.image_path}`}
                  />
                )}
                <Card.Body>
                  <Card.Title className="fw-bold rsvp-detail-title">
                    {eventDetails?.title || "N/A"}
                  </Card.Title>
                  <Card.Text className="rsvp-detail-subtitle fw-medium f-16">
                    <>
                      <Card.Text className="ticket-price mb-2 fw-medium f-16">
                        Non-Members ${Number(eventDetails?.price).toFixed(2)}
                      </Card.Text>
                      <Card.Text className="event-overlay-member-price">
                        {" "}
                        {eventDetails?.member_price
                          ? ` ${new Date(
                            eventDetails?.start_date
                          ).getUTCFullYear()} Members: $${eventDetails?.member_price}`
                          : `Free to ${new Date(
                            eventDetails?.start_date
                          ).getUTCFullYear()} Members`}
                      </Card.Text>
                    </>
                  </Card.Text>
                  <Card.Text>
                    {eventDetails?.description || "No description available."}
                  </Card.Text>
                  <Card.Text className="d-flex align-items-center">
                    <img src={clockImg} alt="clock" />
                    <span className="ms-2">
                      <span>{formatEventDate(eventDetails)}</span>
                    </span>
                  </Card.Text>
                  <Card.Text>
                    <img src={locationImg} alt="location" />
                    <span className="ms-2">{eventDetails?.venue || "N/A"}</span>
                  </Card.Text>
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="fw-bold" style={{ paddingTop: "8px" }}>
                    Event Details
                  </Card.Title>

                  <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                    <Card.Text className="text-muted">
                      No details available for the selected event.
                    </Card.Text>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <EmailVerificationModal
        show={showVerifyModal}
        onHide={() => setShowVerifyModal(false)}
        email={formData.email}
        onVerified={() => setSessionVerifiedEmail(formData.email)}
      />

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
          buttonName={"event"}
          screen={"rsvp"}
          type={"event"}
        />
      )}
    </div>
  );
};

export default Rsvp;
